'use strict';

const libPictView = require('pict-view');
const libProjectEditWorkspace = require('./ProjectEditWorkspace.js');

// The Location section's map + address-search styles (mirrors the read workspace's pw-loc-* / pw-map-host).
const PCW_LOC_CSS = /*css*/`
	.pcw-loc-panel { margin-top: 1.1rem; }
	.pcw-loc-search { margin: 0 0 0.8rem; }
	.pcw-loc-search-row { display: flex; gap: 0.5rem; align-items: stretch; }
	.pcw-addr-input { flex: 1 1 auto; }
	.pcw-addr-status { font-size: 0.82rem; color: var(--theme-color-text-muted, #6b7686); padding: 0.4rem 0.2rem 0.1rem; }
	.pcw-addr-status-error { color: var(--theme-color-status-error, #cf5b5b); }
	.pcw-addr-result { padding: 0.5rem 0.7rem; margin-top: 0.3rem; cursor: pointer; font-size: 0.86rem; color: var(--theme-color-text-secondary, #45505f);
		border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 6px; background: var(--theme-color-background-primary, #fff); }
	.pcw-addr-result:hover { background: var(--theme-color-background-tertiary, #f4f6f9); border-color: var(--theme-color-border-default, #d7dce3); }
	.pcw-loc-coords { display: flex; gap: 1rem; margin: 0 0 0.6rem; flex-wrap: wrap; }
	.pcw-loc-coord { display: flex; flex-direction: column; gap: 0.2rem; }
	.pcw-loc-coord > span { font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); }
	.pcw-loc-coord .input { max-width: 12rem; }
	.pcw-loc-readout { font-size: 0.9rem; color: var(--theme-color-text-secondary, #45505f); margin: 0 0 0.6rem; }
	.pcw-loc-readout code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.1rem 0.4rem; border-radius: 5px; }
	.pcw-map-host { height: 22rem; border-radius: 8px; border: 1px solid var(--theme-color-border-default, #d7dce3); overflow: hidden; }
`;

/**
 * ProjectCreateWorkspace — the bespoke Project CREATE screen (reached by the app-router Create bridge on
 * #/PSRS/Project/Create, which shadows the generic recordset create form so "+ New" opens the curated
 * experience). A thin shell: all data, behavior, and hand-built form HTML live in the ProjectCreateWorkspace
 * provider, which assigns into the #PCW-Body mount. Reuses the Edit workspace's .pew-* CSS verbatim.
 */
class ProjectCreateWorkspace extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.ProjectCreateWorkspace; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			tmpProvider.renderCustomProps();
		}
		return super.onAfterRender(pRenderable);
	}

	/**
	 * Lazily register an EDITABLE pict-section-objecteditor over the new project's CustomProperties, bound to
	 * AppData.ProjectCreateWorkspace.CustomPropsData (mutated in place as the user edits), into this screen's
	 * own mount so a body repaint never destroys it.
	 */
	_ensureCustomPropsView()
	{
		const tmpHash = 'PCW-CustomPropsEditor';
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		const libObjectEditor = require('pict-section-objecteditor');
		this.pict.addView(tmpHash, Object.assign({}, libObjectEditor.default_configuration,
			{
				ViewIdentifier: tmpHash,
				AutoRender: false,
				Editable: true,
				InitialExpandDepth: 2,
				ObjectDataAddress: 'AppData.ProjectCreateWorkspace.CustomPropsData',
				Renderables:
				[
					{
						RenderableHash: 'ObjectEditor-Container',
						TemplateHash: 'ObjectEditor-Container-Template',
						DestinationAddress: '#PCW-CustomPropsHost',
						RenderMethod: 'replace',
					}
				],
			}), libObjectEditor);
		const tmpView = this.pict.views[tmpHash];
		if (tmpView && (typeof tmpView.initialize === 'function')) { tmpView.initialize(); }
		return tmpView;
	}

	/** Render the editable CustomProperties tree into #PCW-CustomPropsHost (called by the provider). */
	mountCustomProps()
	{
		const tmpView = this._ensureCustomPropsView();
		if (!tmpView) { return; }
		tmpView.options.DefaultDestinationAddress = '#PCW-CustomPropsHost';
		tmpView.render();
	}
}

module.exports = ProjectCreateWorkspace;

module.exports.default_configuration =
{
	ViewIdentifier: 'ProjectCreateWorkspace',
	DefaultRenderable: 'ProjectCreateWorkspaceWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	// Reuse the Edit workspace's .pew-* styles verbatim (injection is idempotent by class), plus the Location
	// section's map + address-search styles (the create screen's own; the read workspace's pw-* CSS isn't
	// guaranteed to be injected here).
	CSS: libProjectEditWorkspace.default_configuration.CSS + PCW_LOC_CSS,
	Templates:
	[
		{
			Hash: 'ProjectCreateWorkspace-Wrap',
			Template: /*html*/`<div class="pew-wrap"><div id="PCW-Body"></div><div id="PCW-CustomProps"></div></div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ProjectCreateWorkspaceWrap',
			TemplateHash: 'ProjectCreateWorkspace-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
